import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { styled } from "@glitz/react";
import { spacing, white, black, grey, fontSize, fontWeight } from "../../theme";

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  headerCenter?: React.ReactNode;
  children: React.ReactNode;
}

const Modal: React.FC<ModalProps> = ({ isOpen, onClose, title, headerCenter, children }) => {
  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleEscape);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "";
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const showHeader = title || headerCenter;

  return createPortal(
    <Overlay onClick={onClose}>
      <Content onClick={(e) => e.stopPropagation()}>
        {showHeader && (
          <Header>
            {headerCenter ? (
              <>
                <HeaderCenter>{headerCenter}</HeaderCenter>
                <CloseButton type="button" onClick={onClose} aria-label="Stäng">
                  ×
                </CloseButton>
              </>
            ) : (
              <>
                <Title>{title}</Title>
                <CloseButton type="button" onClick={onClose} aria-label="Stäng">
                  ×
                </CloseButton>
              </>
            )}
          </Header>
        )}
        {children}
      </Content>
    </Overlay>,
    document.body
  );
};

const Overlay = styled.div({
  position: "fixed",
  inset: 0,
  backgroundColor: "rgba(0,0,0,0.5)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 1000,
  padding: { x: spacing.m, y: spacing.m },
});

const Content = styled.div({
  backgroundColor: white,
  borderRadius: "12px",
  maxWidth: "440px",
  width: "100%",
  maxHeight: "90vh",
  overflow: "auto",
  boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
  padding: { x: spacing.xl, y: spacing.xl },
});

const Header = styled.div({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  margin: { bottom: spacing.m },
});

const HeaderCenter = styled.div({
  flex: { grow: 1, shrink: 1, basis: 0 },
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
});

const Title = styled.h2({
  margin: { x: 0, y: 0 },
  fontSize: fontSize.xl,
  fontWeight: fontWeight.bold,
  color: black,
});

const CloseButton = styled.button({
  backgroundColor: "transparent",
  border: { xy: { style: "none" } },
  fontSize: "28px",
  lineHeight: 1,
  color: grey,
  cursor: "pointer",
  padding: { x: spacing.xxs, y: spacing.xxs },
  ":hover": {
    color: black,
  },
});

export default Modal;
